import React, { useState } from 'react';
import { CreditCardIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon, ClipboardCheckIcon, PlusIcon, ClockIcon, CheckCircleIcon, XCircleIcon, DollarSignIcon, ChevronRightIcon, FilterIcon, UserIcon, CalendarIcon, SearchIcon } from 'lucide-react';
import { getDefaultCurrency } from '../../utils/appSettings';
export function ExpenseDashboard({
  locale = 'en-US'
}) {
  const defaultCurrency = getDefaultCurrency();
  // Format currency based on locale
  const formatCurrency = (amount, currency = defaultCurrency) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Format date based on locale
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Dashboard time filter state
  const [timeFilter, setTimeFilter] = useState('30days');
  const [searchTerm, setSearchTerm] = useState('');
  // Sample data for dashboard
  const expenseStats = {
    totalExpenses: 24750.5,
    pendingApproval: 6499.75,
    reimbursed: 18250.75,
    rejected: 1250.5,
    myDrafts: 3,
    myPending: 2,
    myRejected: 1
  };
  // Sample data for recent expense claims
  const recentExpenses = [{
    id: 'EXP-2023-001',
    title: 'Client Meeting - ABC Corp',
    date: '2023-11-10',
    submittedDate: '2023-11-11',
    amount: 245.75,
    status: 'Pending Approval',
    category: 'Travel',
    submittedBy: 'John Doe'
  }, {
    id: 'EXP-2023-002',
    title: 'Office Supplies',
    date: '2023-11-08',
    submittedDate: '2023-11-09',
    amount: 89.99,
    status: 'Approved',
    category: 'Office',
    submittedBy: 'Jane Smith'
  }, {
    id: 'EXP-2023-003',
    title: 'Conference Registration',
    date: '2023-11-05',
    submittedDate: '2023-11-06',
    amount: 599.0,
    status: 'Reimbursed',
    category: 'Training',
    submittedBy: 'Robert Brown'
  }];
  // Sample data for expenses needing your approval
  const pendingApprovalExpenses = [{
    id: 'EXP-2023-007',
    title: 'Customer Workshop',
    date: '2023-11-15',
    submittedDate: '2023-11-16',
    amount: 1280.5,
    category: 'Events',
    submittedBy: 'Sarah Williams',
    department: 'Marketing'
  }, {
    id: 'EXP-2023-008',
    title: 'Trade Show Attendance',
    date: '2023-11-08',
    submittedDate: '2023-11-12',
    amount: 2450.75,
    category: 'Marketing',
    submittedBy: 'Robert Brown',
    department: 'Marketing'
  }];
  // Get status badge styling
  const getStatusBadgeStyle = status => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Reimbursed':
        return 'bg-blue-100 text-blue-800';
      case 'Pending Approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'Draft':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-7707" />;
      case 'Reimbursed':
        return <DollarSignIcon className="h-5 w-5 text-blue-500" data-id="element-7708" />;
      case 'Pending Approval':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-7709" />;
      case 'Rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-7710" />;
      case 'Draft':
      default:
        return <FileTextIcon className="h-5 w-5 text-gray-500" data-id="element-7711" />;
    }
  };
  return <div data-id="element-7712">
      <div className="mb-8 flex justify-between items-center" data-id="element-7713">
        <div data-id="element-7714">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-7715">
            Expense Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-7716">
            Overview of expense claims, approvals, and reimbursements
          </p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-7717">
          <div className="flex items-center" data-id="element-7718">
            <FilterIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-7719" />
            <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="block text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 p-2 border" data-id="element-7720">
              <option value="7days" data-id="element-7721">Last 7 days</option>
              <option value="30days" data-id="element-7722">Last 30 days</option>
              <option value="90days" data-id="element-7723">Last 90 days</option>
              <option value="ytd" data-id="element-7724">Year to date</option>
            </select>
          </div>
          <button onClick={() => window.setCurrentView && window.setCurrentView('add-expense-claim')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7725">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-7726" />
            New Expense
          </button>
        </div>
      </div>

      {/* Summary Cards Section */}
      <div className="mb-8" data-id="element-7727">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-7728">
          Expense Overview
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-7729">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7730">
            <div className="p-5" data-id="element-7731">
              <div className="flex items-center" data-id="element-7732">
                <div className="flex-shrink-0" data-id="element-7733">
                  <CreditCardIcon className="h-6 w-6 text-gray-400" data-id="element-7734" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7735">
                  <dl data-id="element-7736">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7737">
                      Total Expenses
                    </dt>
                    <dd data-id="element-7738">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7739">
                        {formatCurrency(expenseStats.totalExpenses)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} data-id="element-7740">
            <div className="p-5" data-id="element-7741">
              <div className="flex items-center" data-id="element-7742">
                <div className="flex-shrink-0" data-id="element-7743">
                  <ClockIcon className="h-6 w-6 text-yellow-400" data-id="element-7744" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7745">
                  <dl data-id="element-7746">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7747">
                      Pending Approval
                    </dt>
                    <dd data-id="element-7748">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7749">
                        {formatCurrency(expenseStats.pendingApproval)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7750">
            <div className="p-5" data-id="element-7751">
              <div className="flex items-center" data-id="element-7752">
                <div className="flex-shrink-0" data-id="element-7753">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" data-id="element-7754" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7755">
                  <dl data-id="element-7756">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7757">
                      Reimbursed
                    </dt>
                    <dd data-id="element-7758">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7759">
                        {formatCurrency(expenseStats.reimbursed)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7760">
            <div className="p-5" data-id="element-7761">
              <div className="flex items-center" data-id="element-7762">
                <div className="flex-shrink-0" data-id="element-7763">
                  <XCircleIcon className="h-6 w-6 text-red-400" data-id="element-7764" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7765">
                  <dl data-id="element-7766">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7767">
                      Rejected
                    </dt>
                    <dd data-id="element-7768">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7769">
                        {formatCurrency(expenseStats.rejected)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* My Expenses Summary */}
      <div className="mb-8" data-id="element-7770">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-7771">My Expenses</h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3" data-id="element-7772">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7773">
            <div className="p-5" data-id="element-7774">
              <div className="flex items-center" data-id="element-7775">
                <div className="flex-shrink-0" data-id="element-7776">
                  <FileTextIcon className="h-6 w-6 text-gray-400" data-id="element-7777" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7778">
                  <dl data-id="element-7779">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7780">
                      My Drafts
                    </dt>
                    <dd data-id="element-7781">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7782">
                        {expenseStats.myDrafts}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7783">
            <div className="p-5" data-id="element-7784">
              <div className="flex items-center" data-id="element-7785">
                <div className="flex-shrink-0" data-id="element-7786">
                  <ClockIcon className="h-6 w-6 text-yellow-400" data-id="element-7787" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7788">
                  <dl data-id="element-7789">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7790">
                      My Pending Claims
                    </dt>
                    <dd data-id="element-7791">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7792">
                        {expenseStats.myPending}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7793">
            <div className="p-5" data-id="element-7794">
              <div className="flex items-center" data-id="element-7795">
                <div className="flex-shrink-0" data-id="element-7796">
                  <XCircleIcon className="h-6 w-6 text-red-400" data-id="element-7797" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7798">
                  <dl data-id="element-7799">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7800">
                      My Rejected Claims
                    </dt>
                    <dd data-id="element-7801">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7802">
                        {expenseStats.myRejected}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Expenses and Pending Approvals */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-7803">
        {/* Recent Expenses */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-7804">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-7805">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7806">
              Recent Expense Claims
            </h3>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-7807">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-7808">
            {recentExpenses.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-7809">
                No recent expense claims.
              </li> : recentExpenses.map(expense => <li key={expense.id} className="block hover:bg-gray-50 cursor-pointer" onClick={() => window.setCurrentView && window.setCurrentView('expense-claim-detail')} data-id="element-7810">
                  <div className="px-4 py-4 sm:px-6" data-id="element-7811">
                    <div className="flex items-center justify-between" data-id="element-7812">
                      <div className="flex items-center space-x-3" data-id="element-7813">
                        <div className="flex-shrink-0" data-id="element-7814">
                          {getStatusIcon(expense.status)}
                        </div>
                        <div data-id="element-7815">
                          <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-7816">
                            {expense.title}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-7817">
                            {expense.id} • Submitted{' '}
                            {formatDate(expense.submittedDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4" data-id="element-7818">
                        <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeStyle(expense.status)}`} data-id="element-7819">
                          {expense.status}
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-7820">
                          {formatCurrency(expense.amount)}
                        </p>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-7821" />
                      </div>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
        {/* Expenses Needing Your Approval */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-7822">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-7823">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7824">
              Needing Your Approval
            </h3>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-7825">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-7826">
            {pendingApprovalExpenses.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-7827">
                No expenses pending your approval.
              </li> : pendingApprovalExpenses.map(expense => <li key={expense.id} className="block hover:bg-gray-50 cursor-pointer" onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} data-id="element-7828">
                  <div className="px-4 py-4 sm:px-6" data-id="element-7829">
                    <div className="flex items-center justify-between" data-id="element-7830">
                      <div className="flex items-center space-x-3" data-id="element-7831">
                        <div className="flex-shrink-0" data-id="element-7832">
                          <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-7833" />
                        </div>
                        <div data-id="element-7834">
                          <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-7835">
                            {expense.title}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-7836">
                            {expense.id} • Submitted{' '}
                            {formatDate(expense.submittedDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4" data-id="element-7837">
                        <p className="text-sm font-medium text-gray-900" data-id="element-7838">
                          {formatCurrency(expense.amount)}
                        </p>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-7839" />
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between" data-id="element-7840">
                      <div className="sm:flex" data-id="element-7841">
                        <p className="flex items-center text-sm text-gray-500" data-id="element-7842">
                          <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-7843" />
                          {expense.submittedBy} • {expense.department}
                        </p>
                      </div>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>

      {/* Expense Category Distribution */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8" data-id="element-7844">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-7845">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7846">
            Expense Category Distribution
          </h3>
        </div>
        <div className="p-4" data-id="element-7847">
          <div className="h-64 flex items-center justify-center" data-id="element-7848">
            <div className="w-full max-w-3xl" data-id="element-7849">
              {/* Simple bar chart for expense categories */}
              <div className="space-y-4" data-id="element-7850">
                <div data-id="element-7851">
                  <div className="flex items-center justify-between text-sm" data-id="element-7852">
                    <span data-id="element-7853">Travel</span>
                    <span className="font-medium" data-id="element-7854">
                      {formatCurrency(9850.25)} (39.8%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-7855">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-7856">
                      <div style={{
                      width: '39.8%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500" data-id="element-7857"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-7858">
                  <div className="flex items-center justify-between text-sm" data-id="element-7859">
                    <span data-id="element-7860">Training</span>
                    <span className="font-medium" data-id="element-7861">
                      {formatCurrency(4750.0)} (19.2%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-7862">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-7863">
                      <div style={{
                      width: '19.2%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500" data-id="element-7864"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-7865">
                  <div className="flex items-center justify-between text-sm" data-id="element-7866">
                    <span data-id="element-7867">Meals</span>
                    <span className="font-medium" data-id="element-7868">
                      {formatCurrency(3250.5)} (13.1%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-7869">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-7870">
                      <div style={{
                      width: '13.1%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500" data-id="element-7871"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-7872">
                  <div className="flex items-center justify-between text-sm" data-id="element-7873">
                    <span data-id="element-7874">Marketing</span>
                    <span className="font-medium" data-id="element-7875">
                      {formatCurrency(2450.75)} (9.9%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-7876">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-7877">
                      <div style={{
                      width: '9.9%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500" data-id="element-7878"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-7879">
                  <div className="flex items-center justify-between text-sm" data-id="element-7880">
                    <span data-id="element-7881">Software</span>
                    <span className="font-medium" data-id="element-7882">
                      {formatCurrency(2150.0)} (8.7%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-7883">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-7884">
                      <div style={{
                      width: '8.7%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-yellow-500" data-id="element-7885"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8" data-id="element-7886">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-7887">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7888">
            Quick Actions
          </h3>
        </div>
        <div className="p-4" data-id="element-7889">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-7890">
            <button onClick={() => window.setCurrentView && window.setCurrentView('add-expense-claim')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-7891">
              <div className="flex flex-col items-center" data-id="element-7892">
                <div className="bg-indigo-100 p-3 rounded-full mb-3" data-id="element-7893">
                  <PlusIcon className="h-6 w-6 text-indigo-600" data-id="element-7894" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-7895">
                  New Expense Claim
                </span>
              </div>
            </button>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-7896">
              <div className="flex flex-col items-center" data-id="element-7897">
                <div className="bg-yellow-100 p-3 rounded-full mb-3" data-id="element-7898">
                  <ClipboardCheckIcon className="h-6 w-6 text-yellow-600" data-id="element-7899" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-7900">
                  Review Approvals
                </span>
              </div>
            </button>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-7901">
              <div className="flex flex-col items-center" data-id="element-7902">
                <div className="bg-blue-100 p-3 rounded-full mb-3" data-id="element-7903">
                  <FileTextIcon className="h-6 w-6 text-blue-600" data-id="element-7904" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-7905">
                  View All Expenses
                </span>
              </div>
            </button>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-reports')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-7906">
              <div className="flex flex-col items-center" data-id="element-7907">
                <div className="bg-green-100 p-3 rounded-full mb-3" data-id="element-7908">
                  <TrendingUpIcon className="h-6 w-6 text-green-600" data-id="element-7909" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-7910">
                  Run Reports
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>;
}